import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Text,
  Badge,
  Alert,
  AlertDescription,
  AlertTitle,
  AlertIcon,
  Textarea,
  IconButton,
  useColorModeValue,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FiDownload,
  FiTrash2,
  FiSave,
  FiRefreshCcw,
  FiUpload,
  FiEdit,
} from 'react-icons/fi';
import AdminLayout from '../../layouts/AdminLayout';
import {
  listManualCompetitions,
  createManualCompetition,
  updateManualCompetition,
  deleteManualCompetition,
  getMatchesTemplateUrl,
  getTablesTemplateUrl,
  importManualMatches,
  importManualTables,
  importManualMatchesJSON,
  importManualTablesJSON,
  ManualCompetition,
  ManualCompetitionInput,
} from '../../services/manualFacrAdmin';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import UploadPanel, { UploadPanelFile } from '../../components/common/UploadPanel';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { HelpTooltipCard } from '../../components/common/HelpTooltipCard';

const exampleMatchesJson = `{
  "items": [
    {
      "competition_code": "A1A",
      "competition_external_id": "",
      "round": "1. kolo",
      "is_home": "home",
      "opponent_name": "FC Hosté",
      "opponent_club_link": "https://www.fotbal.cz/klub/...",
      "external_match_id": "MATCH-1",
      "kickoff_date": "2024-08-01",
      "kickoff_time": "17:00",
      "score_fulltime": "",
      "score_halftime": "",
      "match_link": "",
      "venue": "Domácí stadion",
      "note": ""
    }
  ]
}`;

const exampleTablesJson = `{
  "items": [
    {
      "competition_code": "A1A",
      "competition_external_id": "",
      "rank": "1",
      "team_name": "Váš klub",
      "team_club_link": "https://www.fotbal.cz/klub/...",
      "played": "1",
      "wins": "1",
      "draws": "0",
      "losses": "0",
      "score": "3:0",
      "points": "3"
    }
  ]
}`;

const ManualFacrAdminPage: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { data: publicSettings } = usePublicSettings();
  const isManualClubDataMode = (publicSettings?.club_data_mode || '').toLowerCase() === 'manual';
  const isManualFacrMode = process.env.REACT_APP_MANUAL_FACR_MODE === 'true';
  const { confirm } = useConfirmDialog();

  // Show message if manual FACR mode is not enabled
  if (!isManualFacrMode) {
    return (
      <Box maxW="1400px" mx="auto" p={6}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Manuální FAČR režim není aktivní</AlertTitle>
            <AlertDescription>
              Tato stránka je dostupná pouze při zapnutém manuálním FAČR režimu.
              Kontaktujte administrátora pro aktivaci této funkce.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  const { data, isLoading, refetch } = useQuery<ManualCompetition[]>({
    queryKey: ['manual-competitions'],
    queryFn: listManualCompetitions,
  });

  const competitions = useMemo(() => data || [], [data]);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ManualCompetitionInput>({
    code: '',
    name: '',
    external_id: '',
    team_count: '',
  });
  const [bulkCompetitionLine, setBulkCompetitionLine] = useState('');
  const [matchesJson, setMatchesJson] = useState('');
  const [tablesJson, setTablesJson] = useState('');
  const [importingMatchesJson, setImportingMatchesJson] = useState(false);
  const [importingTablesJson, setImportingTablesJson] = useState(false);
  const [templateKind, setTemplateKind] = useState<'matches' | 'tables' | null>(null);
  const {
    isOpen: isTemplateHelpOpen,
    onOpen: openTemplateHelp,
    onClose: closeTemplateHelp,
  } = useDisclosure();

  const resetForm = () => {
    setEditingId(null);
    setForm({
      code: '',
      name: '',
      external_id: '',
      team_count: '',
    });
    setBulkCompetitionLine('');
  };

  const parseBulkCompetitionLine = (raw: string): { code: string; name: string; teamCount?: string } | null => {
    const value = (raw || '').trim();
    if (!value) return null;
    // First, support multi-line paste: code on first line, name on middle lines, team count on last line.
    const lines = value.split(/\r?\n/).map((l) => l.trim()).filter((l) => l !== '');
    if (lines.length >= 3) {
      const code = lines[0];
      const last = lines[lines.length - 1];
      const nameLines = lines.slice(1, lines.length - 1);
      const name = nameLines.join(' ');
      const m = last.match(/^\d{1,3}$/);
      const teamCount = m ? m[0] : undefined;
      if (!code || !name) return null;
      return { code, name, teamCount };
    }
    // Fallback: single-line format, e.g. "A1A SATUM 5. liga mužů 16"
    const tokens = value.replace(/\t+/g, ' ').split(/\s+/).filter((t) => t !== '');
    if (tokens.length < 2) return null;
    const code = tokens[0];
    let teamCount: string | undefined;
    const last = tokens[tokens.length - 1];
    if (/^\d{1,3}$/.test(last)) {
      teamCount = last;
      tokens.pop();
    }
    const nameTokens = tokens.slice(1);
    const name = nameTokens.join(' ').trim();
    if (!code || !name) return null;
    return { code, name, teamCount };
  };

  const handleBulkCompetitionApply = () => {
    const parsed = parseBulkCompetitionLine(bulkCompetitionLine);
    if (!parsed) {
      toast({
        title: 'Nelze rozpoznat soutěž',
        description: 'Zkontrolujte formát. Očekává se například "A1A SATUM 5. liga mužů 16" nebo tři řádky: kód, název, počet týmů.',
        status: 'warning',
      });
      return;
    }
    setForm((prev) => ({
      ...prev,
      code: parsed.code || prev.code,
      name: parsed.name || prev.name,
      team_count: parsed.teamCount ?? prev.team_count,
    }));
  };

  const handleImportMatchesJSON = async () => {
    const raw = matchesJson.trim();
    if (!raw) {
      toast({ title: 'Vložte JSON payload pro import zápasů', status: 'warning' });
      return;
    }
    let payload: any;
    try {
      payload = JSON.parse(raw);
    } catch (err: any) {
      toast({
        title: 'Neplatný JSON',
        description: 'Zkontrolujte prosím syntaxi JSON payloadu.',
        status: 'error',
      });
      return;
    }
    setImportingMatchesJson(true);
    try {
      const res = await importManualMatchesJSON(payload);
      toast({
        title: 'JSON import zápasů dokončen',
        description: `Nově: ${res.imported}, aktualizováno: ${res.updated}.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      if (res.errors && res.errors.length) {
        console.warn('Chyby JSON importu zápasů:', res.errors);
      }
      await queryClient.invalidateQueries({ queryKey: ['manual-competitions'] });
    } catch (err: any) {
      toast({
        title: 'JSON import zápasů selhal',
        description: err?.message || 'Zkontrolujte formát JSON payloadu.',
        status: 'error',
      });
    } finally {
      setImportingMatchesJson(false);
    }
  };

  const handleImportTablesJSON = async () => {
    const raw = tablesJson.trim();
    if (!raw) {
      toast({ title: 'Vložte JSON payload pro import tabulek', status: 'warning' });
      return;
    }
    let payload: any;
    try {
      payload = JSON.parse(raw);
    } catch (err: any) {
      toast({
        title: 'Neplatný JSON',
        description: 'Zkontrolujte prosím syntaxi JSON payloadu.',
        status: 'error',
      });
      return;
    }
    setImportingTablesJson(true);
    try {
      const res = await importManualTablesJSON(payload);
      toast({
        title: 'JSON import tabulek dokončen',
        description: `Načteno řádků: ${res.imported}.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      if (res.errors && res.errors.length) {
        console.warn('Chyby JSON importu tabulek:', res.errors);
      }
      await queryClient.invalidateQueries({ queryKey: ['manual-competitions'] });
    } catch (err: any) {
      toast({
        title: 'JSON import tabulek selhal',
        description: err?.message || 'Zkontrolujte formát JSON payloadu.',
        status: 'error',
      });
    } finally {
      setImportingTablesJson(false);
    }
  };

  const fillFormFromCompetition = (c: ManualCompetition) => {
    setEditingId(c.id);
    setForm({
      code: c.code || '',
      name: c.name || '',
      external_id: c.external_id || '',
      team_count: c.team_count || '',
    });
  };

  const upsertMutation = useMutation({
    mutationFn: async () => {
      const payload: ManualCompetitionInput = {
        code: form.code.trim(),
        name: form.name.trim(),
        external_id: form.external_id.trim(),
        team_count: form.team_count?.trim() || undefined,
      };
      if (!payload.code || !payload.name || !payload.external_id) {
        throw new Error('Kód, název a external_id jsou povinné.');
      }
      if (editingId != null) {
        return updateManualCompetition(editingId, payload);
      }
      return createManualCompetition(payload);
    },
    onSuccess: async () => {
      toast({ title: 'Soutěž uložena', status: 'success' });
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ['manual-competitions'] });
    },
    onError: (err: any) => {
      toast({
        title: 'Uložení selhalo',
        description: err?.message || 'Zkuste to prosím znovu.',
        status: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await deleteManualCompetition(id);
    },
    onSuccess: async () => {
      toast({ title: 'Soutěž smazána', status: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['manual-competitions'] });
      if (editingId != null) {
        resetForm();
      }
    },
    onError: (err: any) => {
      toast({
        title: 'Smazání selhalo',
        description: err?.message || 'Zkuste to prosím znovu.',
        status: 'error',
      });
    },
  });

  const handleSubmitCompetition = (e: React.FormEvent) => {
    e.preventDefault();
    upsertMutation.mutate();
  };

  const handleDeleteCompetition = async (c: ManualCompetition) => {
    const ok = await confirm({
      title: 'Smazat soutěž',
      message: `Opravdu chcete smazat soutěž ${c.code} – ${c.name}?\nBudou také odstraněny všechny ruční zápasy a tabulky.`,
      confirmText: 'Smazat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;
    deleteMutation.mutate(c.id);
  };

  const uploadMatchesFile = async (file: File): Promise<UploadPanelFile> => {
    try {
      const res = await importManualMatches(file);
      toast({
        title: 'Import zápasů dokončen',
        description: `Nově: ${res.imported}, aktualizováno: ${res.updated}.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      if (res.errors && res.errors.length) {
        console.warn('Chyby importu zápasů:', res.errors);
      }
      await queryClient.invalidateQueries({ queryKey: ['manual-competitions'] });
    } catch (err: any) {
      toast({
        title: 'Import zápasů selhal',
        description: err?.message || 'Zkontrolujte formát CSV nebo XLSX.',
        status: 'error',
      });
      throw err;
    }

    return {
      url: file.name,
      name: file.name,
      type: file.type || 'application/octet-stream',
      size: file.size,
    };
  };

  const uploadTablesFile = async (file: File): Promise<UploadPanelFile> => {
    try {
      const res = await importManualTables(file);
      toast({
        title: 'Import tabulek dokončen',
        description: `Načteno řádků: ${res.imported}.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      if (res.errors && res.errors.length) {
        console.warn('Chyby importu tabulek:', res.errors);
      }
      await queryClient.invalidateQueries({ queryKey: ['manual-competitions'] });
    } catch (err: any) {
      toast({
        title: 'Import tabulek selhal',
        description: err?.message || 'Zkontrolujte formát CSV nebo XLSX.',
        status: 'error',
      });
      throw err;
    }

    return {
      url: file.name,
      name: file.name,
      type: file.type || 'application/octet-stream',
      size: file.size,
    };
  };

  const handleDownload = (kind: 'matches' | 'tables') => {
    const url = kind === 'matches' ? getMatchesTemplateUrl() : getTablesTemplateUrl();
    window.open(url, '_blank');
    setTemplateKind(kind);
    openTemplateHelp();
  };

  return (
    <AdminLayout requireAdmin={true}>
      <Box maxW="1400px" mx="auto">
        <Box mb={6}>
          <Heading size="lg" mb={1}>Krok 2/2: Manuální data soutěží (FAČR)</Heading>
          <Text color="gray.600" fontSize="xs" mb={1}>
            Po úvodním nastavení (krok 1) zde nahrajete zápasy a tabulky z CSV / Excel (XLSX) souborů nebo JSON payloadů, aby se zobrazovaly na webu.
          </Text>
          <Text color="gray.600" fontSize="sm">
            Zde můžete spravovat soutěže, do kterých váš klub patří, a hromadně importovat zápasy i tabulky z CSV
            nebo Excelu (XLSX). V manuálním režimu backend nevolá FACR / fotbal.cz – veškerá data pochází z těchto
            ručních vstupů.
          </Text>
        </Box>

        <Alert status="info" borderRadius="md" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>Jak postupovat</AlertTitle>
            <AlertDescription fontSize="sm">
              1) Přidejte soutěže (kód, název, externí ID z odkazu na soutěž).
              2) Stáhněte si XLSX šablony pro zápasy a tabulky, otevřete je v Excelu / Google Sheets,
              doplňte data a soubor pak nahrajte zpět jako XLSX nebo případně uložte do CSV – import podporuje oba formáty.
              Sloupec <code>kod_souteze</code> nebo <code>id_souteze_uuid</code> musí odpovídat zde založeným soutěžím.
            </AlertDescription>
          </Box>
        </Alert>

        <Flex gap={6} align="flex-start" direction={{ base: 'column', lg: 'row' }}>
          {/* Competition form */}
          <Box flex="1" bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
            <Heading size="md" mb={4}>{editingId ? 'Upravit soutěž' : 'Nová soutěž'}</Heading>
            <form onSubmit={handleSubmitCompetition}>
              <VStack align="stretch" spacing={4}>
                <FormControl>
                  <FormLabel>Vložit vše z FACR řádku</FormLabel>
                  <HStack align="flex-start" spacing={2}>
                    <Input
                      value={bulkCompetitionLine}
                      onChange={(e) => setBulkCompetitionLine(e.target.value)}
                      placeholder="A1A SATUM 5. liga mužů 16"
                    />
                    <Button size="sm" onClick={handleBulkCompetitionApply}>
                      Vyplnit pole
                    </Button>
                  </HStack>
                  <FormHelperText fontSize="xs">
                    Vložte řádek z tabulky „Nová soutěž“ (kód, název, počet týmů). Podporováno je i víceřádkové vložení:
                    první řádek kód, druhý název, třetí počet týmů.
                  </FormHelperText>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Kód soutěže (např. A1A)</FormLabel>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    placeholder="Kód soutěže (FACR)"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Název soutěže</FormLabel>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Např. SATUM 5. liga mužů"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>
                    <HStack spacing={2} align="center">
                      <Box as="span">Externí ID soutěže (UUID z odkazu)</Box>
                      <HelpTooltipCard
                        label="Nápověda k externímu ID soutěže"
                        title="Jak získat externí ID (UUID) soutěže"
                        items={[
                          'Použijte libovolný odkaz na soutěž na fotbal.cz (např. se segmentem /zapas/, /hlavni/ nebo /table/).',
                          'Můžete vložit celý odkaz – systém z něj automaticky vybere samotné UUID.',
                          'UUID pak slouží k vygenerování odkazů na zápasy i tabulku dané soutěže.',
                        ]}
                      />
                    </HStack>
                  </FormLabel>
                  <Input
                    value={form.external_id}
                    onChange={(e) => setForm((f) => ({ ...f, external_id: e.target.value }))}
                    placeholder="UUID nebo celý odkaz na soutěž z fotbal.cz"
                  />
                  <FormHelperText fontSize="xs">
                    Např. z adresy
                    <code> https://www.fotbal.cz/souteze/turnaje/hlavni/e3127865-a109-45cd-9048-3e6429e2eb11</code>
                    nebo
                    <code> https://www.fotbal.cz/souteze/turnaje/table/e3127865-a109-45cd-9048-3e6429e2eb11</code>
                    stačí vložit celý odkaz – UUID se vybere automaticky.
                  </FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Počet týmů (volitelné)</FormLabel>
                  <Input
                    value={form.team_count}
                    onChange={(e) => setForm((f) => ({ ...f, team_count: e.target.value }))}
                    placeholder="Např. 14"
                  />
                </FormControl>
                <HStack spacing={3} pt={2}>
                  <Button
                    type="submit"
                    colorScheme="blue"
                    leftIcon={<FiSave />}
                    isLoading={upsertMutation.isPending}
                  >
                    {editingId ? 'Uložit změny' : 'Přidat soutěž'}
                  </Button>
                  {editingId != null && (
                    <Button variant="outline" onClick={resetForm}>
                      Zrušit úpravy
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    leftIcon={<FiRefreshCcw />}
                    onClick={() => refetch()}
                  >
                    Obnovit seznam
                  </Button>
                </HStack>
              </VStack>
            </form>
            <Box mt={4}>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Pokročilé: můžete také vložit JSON payload (např. <code>{'{"items":[{"competition_code":"A1A","round":"1. kolo",...}]}'}</code>) podle API schématu a importovat jej přímo.
              </Text>
              <Textarea
                rows={4}
                value={matchesJson}
                onChange={(e) => setMatchesJson(e.target.value)}
                placeholder='{"items":[{"competition_code":"A1A","round":"1. kolo",...}]}'
              />
              <HStack spacing={3} mt={2}>
                <Button
                  size="sm"
                  leftIcon={<FiUpload />}
                  onClick={handleImportMatchesJSON}
                  isLoading={importingMatchesJson}
                  colorScheme="purple"
                >
                  Importovat JSON
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setMatchesJson(exampleMatchesJson)}
                >
                  Vložit ukázku
                </Button>
              </HStack>
            </Box>
          </Box>

          {/* Import panels */}
          <VStack flex="1" spacing={6} align="stretch">
            <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
              <Heading size="md" mb={3}>Import zápasů z Excelu (XLSX) nebo CSV</Heading>
              <Text fontSize="sm" color="gray.600" mb={1}>
                XLSX šablona obsahuje list <code>Zápasy</code> se sloupci pro všechny soutěže, list <code>Souteze</code>
                s přehledem soutěží a list <code>Tymy</code> s názvy týmů z uložených tabulek. Můžete použít jeden soubor
                pro všechny soutěže nebo jeden soubor na každou soutěž.
              </Text>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Sloupce v šabloně (list <code>Zápasy</code>): <code>kod_souteze</code>, <code>id_souteze_uuid</code>, <code>kolo</code>, <code>doma_venku</code>, <code>nazev_soupere</code>, <code>odkaz_klub_soupere</code>, <code>id_zapasu</code>, <code>datum</code>, <code>cas</code>, <code>vysledek</code>, <code>vysledek_polocas</code>, <code>odkaz_na_zapas</code>, <code>hriste</code>.
              </Text>
              <UnorderedList>
                <ListItem><code>vysledek</code> a <code>vysledek_polocas</code> jsou volitelné texty (např. „3:1“ nebo „3:1 (1:0)“).</ListItem>
                <ListItem><code>hriste</code> je volitelné pole pro popis hřiště (město, areál apod.).</ListItem>
              </UnorderedList>
              <HStack spacing={3} mb={3}>
                <Button
                  size="sm"
                  leftIcon={<FiDownload />}
                  onClick={() => handleDownload('matches')}
                >
                  Stáhnout šablonu pro Excel (XLSX)
                </Button>
              </HStack>
              <UploadPanel
                label="Soubor se zápasy (CSV / XLSX)"
                description="Nahrajte CSV nebo Excel (XLSX) soubor vyplněný podle stažené šablony. Import se spustí automaticky po nahrání."
                accept=".csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
                multiple={false}
                maxFiles={1}
                allowUrlImport={false}
                uploadFn={uploadMatchesFile}
              />
              <Text mt={2} fontSize="xs" color="gray.500">
                Šablona se stahuje jako Excel (XLSX). Otevřete ji v Excelu nebo Google Sheets, vyplňte list <code>Zápasy</code>
                a soubor pak nahrajte zpět jako XLSX nebo případně uložte do CSV – import podporuje oba formáty.
              </Text>
            </Box>

            <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
              <Heading size="md" mb={3}>Import tabulek z Excelu (XLSX) nebo CSV</Heading>
              <Text fontSize="sm" color="gray.600" mb={1}>
                XLSX šablona obsahuje list <code>Tabulky</code> s řádky tabulek, list <code>Souteze</code> a list
                <code> Tymy</code> s názvy týmů. V polích <code>kod_souteze</code> a <code>nazev_tymu</code> jsou k dispozici
                rozbalovací nabídky podle těchto dat. Každý řádek reprezentuje jeden tým v tabulce.
              </Text>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Sloupce v šabloně (list <code>Tabulky</code>): <code>kod_souteze</code>, <code>id_souteze_uuid</code>, <code>poradi</code>, <code>nazev_tymu</code>, <code>odkaz_na_klub</code>, <code>zapasy</code>, <code>vyhry</code>, <code>remizy</code>, <code>prohry</code>, <code> skore</code>, <code>body</code>.
              </Text>
              <HStack spacing={3} mb={3}>
                <Button
                  size="sm"
                  leftIcon={<FiDownload />}
                  onClick={() => handleDownload('tables')}
                >
                  Stáhnout šablonu pro Excel (XLSX)
                </Button>
              </HStack>
              <UploadPanel
                label="Soubor s tabulkami (CSV / XLSX)"
                description="Nahrajte CSV nebo Excel (XLSX) soubor vyplněný podle stažené šablony. Import se spustí automaticky po nahrání."
                accept=".csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
                multiple={false}
                maxFiles={1}
                allowUrlImport={false}
                uploadFn={uploadTablesFile}
              />
              <Text mt={2} fontSize="xs" color="gray.500">
                Šablona se stahuje jako Excel (XLSX). Otevřete ji v Excelu nebo Google Sheets, vyplňte list <code>Tabulky</code>
                a soubor pak nahrajte zpět jako XLSX nebo případně uložte do CSV – import podporuje oba formáty.
              </Text>
              <Box mt={4}>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Pokročilé: můžete také importovat tabulky z JSON payloadu (pole <code>items</code> se stejnými sloupci jako v CSV).
                </Text>
                <Textarea
                  rows={4}
                  value={tablesJson}
                  onChange={(e) => setTablesJson(e.target.value)}
                  placeholder='{"items":[{"competition_code":"A1A","team_name":"Váš klub",...}]}'
                />
                <HStack spacing={3} mt={2}>
                  <Button
                    size="sm"
                    leftIcon={<FiUpload />}
                    onClick={handleImportTablesJSON}
                    isLoading={importingTablesJson}
                    colorScheme="purple"
                  >
                    Importovat JSON
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setTablesJson(exampleTablesJson)}
                  >
                    Vložit ukázku
                  </Button>
                </HStack>
              </Box>
            </Box>
          </VStack>
        </Flex>

        <Box mt={8} bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
          <Heading size="md" mb={4}>Přehled soutěží</Heading>
          {isLoading ? (
            <HStack spacing={3}>
              <Spinner size="sm" />
              <Text>Načítám soutěže…</Text>
            </HStack>
          ) : competitions.length === 0 ? (
            <Text color="gray.500" fontSize="sm">Zatím nejsou založeny žádné soutěže.</Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Kód</Th>
                    <Th>Název</Th>
                    <Th>External ID</Th>
                    <Th>Zápasy</Th>
                    <Th>Tabulka</Th>
                    <Th>Počet týmů</Th>
                    <Th isNumeric>Akce</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {competitions.map((c) => (
                    <Tr key={c.id}>
                      <Td>
                        <Badge colorScheme="blue" fontFamily="mono">{c.code}</Badge>
                      </Td>
                      <Td>{c.name}</Td>
                      <Td fontFamily="mono" fontSize="xs">{c.external_id}</Td>
                      <Td maxW="260px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{c.matches_link}</Td>
                      <Td maxW="260px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{c.table_link}</Td>
                      <Td>{c.team_count}</Td>
                      <Td isNumeric>
                        <HStack spacing={2} justify="flex-end">
                          <IconButton
                            aria-label="Upravit"
                            icon={<FiEdit />}
                            size="xs"
                            variant="ghost"
                            onClick={() => fillFormFromCompetition(c)}
                          />
                          <IconButton
                            aria-label="Smazat"
                            icon={<FiTrash2 />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteCompetition(c)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>

      <Modal isOpen={isTemplateHelpOpen} onClose={closeTemplateHelp} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {templateKind === 'matches'
              ? 'Jak pracovat se šablonou zápasů (XLSX)'
              : 'Jak pracovat se šablonou tabulek (XLSX)'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              mb={4}
              position="relative"
              w="100%"
              pb="56.25%"
              borderRadius="md"
              overflow="hidden"
              bg={useColorModeValue('gray.100', 'gray.700')}
            >
              <Flex
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                align="center"
                justify="center"
              >
                <Text fontSize="sm" color="gray.600" textAlign="center" px={4}>
                  Zde bude krátké video s návodem, jak šablonu vyplnit. Prozatím použijte textový návod níže.
                </Text>
              </Flex>
            </Box>

            {templateKind === 'matches' ? (
              <>
                <Text fontSize="sm" mb={2}>
                  Šablona pro zápasy obsahuje list <code>Zápasy</code> pro samotné zápasy, list <code>Souteze</code>
                  s přehledem soutěží a list <code>Tymy</code> s názvy týmů. Z těchto listů se doplňují dropdowny v polích
                  <code> kod_souteze</code> a <code>nazev_soupere</code>.
                </Text>
                <UnorderedList spacing={1} fontSize="sm" mt={2}>
                  <ListItem>
                    Vyberte <code>kod_souteze</code> z rozbalovacího seznamu – musí odpovídat soutěžím v přehledu na této stránce.
                  </ListItem>
                  <ListItem>
                    V poli <code>nazev_soupere</code> se zobrazují týmy z listu <code>Tymy</code> a jsou automaticky
                    filtrované podle zvoleného <code>kod_souteze</code> v daném řádku.
                  </ListItem>
                  <ListItem>
                    <code>id_souteze_uuid</code> je volitelné „pokročilé“ pole – typicky stačí vyplnit pouze <code>kod_souteze</code>.
                  </ListItem>
                  <ListItem>
                    V poli <code>doma_venku</code> použijte hodnoty z dropdownu (např. <code>doma</code> / <code>venku</code>).
                  </ListItem>
                  <ListItem>
                    Pro <code>id_zapasu</code> můžete buď vložit čisté ID, nebo jednoduše vyplnit <code>odkaz_na_zapas</code>
                    celým URL z fotbal.cz – systém z něj ID automaticky vyextrahuje.
                  </ListItem>
                  <ListItem>
                    Datum a čas vyplňujte ve formátu <code>YYYY-MM-DD</code> a <code>HH:MM</code> (např. <code>2024-08-01</code>, <code>17:00</code>).
                  </ListItem>
                </UnorderedList>
              </>
            ) : (
              <>
                <Text fontSize="sm" mb={2}>
                  Šablona pro tabulky obsahuje list <code>Tabulky</code>, list <code>Souteze</code> a list
                  <code> Tymy</code>. V polích <code>kod_souteze</code> a <code>nazev_tymu</code> jsou dropdowny
                  vycházející z těchto dat – názvy týmů jsou automaticky filtrované podle soutěže v daném řádku.
                  Každý řádek reprezentuje jeden tým v tabulce.
                </Text>
                <UnorderedList spacing={1} fontSize="sm" mt={2}>
                  <ListItem>
                    Vyberte <code>kod_souteze</code> z dropdownu podle soutěže, ke které tabulka patří.
                  </ListItem>
                  <ListItem>
                    <code>id_souteze_uuid</code> je opět volitelné – stačí se držet <code>kod_souteze</code>.
                  </ListItem>
                  <ListItem>
                    Pro <code>nazev_tymu</code> použijte stejný název, jaký chcete zobrazovat v tabulce na webu.
                  </ListItem>
                  <ListItem>
                    Číselné hodnoty jako <code>zapasy</code>, <code>vyhry</code>, <code>remizy</code>, <code>prohry</code>,
                    <code> skore</code> a <code>body</code> vyplňujte jako text/číslo přesně tak, jak je chcete ukazovat v přehledu.
                  </ListItem>
                </UnorderedList>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeTemplateHelp} colorScheme="blue">
              Zavřít návod
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
};

export default ManualFacrAdminPage;
